// Package Imports
// prettier-ignore
import { useOne, useTranslate, useUpdate, } from '@refinedev/core';
import { useForm } from '@refinedev/antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Card } from 'antd';
import { EditOutlined } from '@ant-design/icons';

// Project Imports
// import resourceName from 'core/common/resourceName';
import RuleGroupsForm from 'pages/ruleGroups/RuleGroupsForm';
import { useRulesContext } from 'contexts/rulesContext/RulesContext';
import apiEndpoint from 'core/common/apiEndPoints';
import constants from 'core/constants/Constants';
import openNotificationWithIcon from 'core/utils/notification';
import CustomModal from 'components/modal/modal';
// import { EditOutlined } from '@ant-design/icons';
import getFooterButton from 'core/utils/getFooterButton';
import Loading from 'components/loading/loading';

const RuleGroupsCreate: React.FC<{
  type: string;
  id?: string;
  rulesData?: any;
}> = ({ type, id, rulesData }) => {
  const t = useTranslate();

  const {
    rulePlanDataObj,
    setRefetchRuleDetails,
    refetchRuleDetails,
    // rulesDetails,
  } = useRulesContext();
  const { mutate } = useUpdate();
  const [allowEdit, setAllowEdit] = useState(false);
  const [editId, setEditId] = useState<string>('');
  const [ruleGroupDate, setRuleGroupDate] = useState([]);
  const [pageType, setPageType] = useState<string>(
    type === 'Edit' ? 'Show' : type
  );
  const [saveLoading, setSaveLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [statusModal, setStatusModal] = useState(false);

  const { data: ruleGroupData, isFetching: ruleGroupFetching } = useOne({
    resource: `${type === 'Show' && rulePlanDataObj?.view_unique_id ? apiEndpoint.ruleGroup : apiEndpoint.ruleGroupDraft}`,
    id: editId,
    queryOptions: {
      enabled: allowEdit && !!id,
    },
  });
  const ruleData = ruleGroupData?.data?.results;
  // prettier-ignore

  function resetFieldsWithoutDate(form, startDate, endDate) {
    form.setFieldsValue({
      rule_type: null,
      rule_group_name: null,
      rule_group_description: null,
      rule_group_date: [dayjs(startDate), dayjs(endDate)],
    });
  }

  // prettier-ignore
  const { onFinish, formProps, saveButtonProps, formLoading, form } = useForm({
    resource: `${apiEndpoint.ruleGroupDraft}`,
    redirect: false,
    action: 'create',
    successNotification: false,
    errorNotification: false,
    onMutationSuccess: (data) => {
      const message = data?.data?.message || t('notifications.createSuccess');
      resetFieldsWithoutDate(
        // prettier-ignore
        form, rulePlanDataObj.rule_plan_start_date, rulePlanDataObj.rule_plan_end_date
      );
      setAllowEdit(false);

      // prettier-ignore
      openNotificationWithIcon(constants.success, t('notifications.mutationSuccess'), message);
      setRefetchRuleDetails(!refetchRuleDetails);

    },
    onMutationError(error) {
      const message = error?.message || t('notifications.createError');
      // prettier-ignore
      openNotificationWithIcon(constants.error, t('notifications.mutationError'), message);
      setSaveLoading(false)
    },
  });

  const onFinishHandler = async (value: any) => {
    setSaveLoading(true);
    const getDaterange = value?.rule_group_date;
    let startDate = dayjs();
    let endDate = dayjs();
    if (getDaterange) {
      startDate = getDaterange?.[0];
      endDate = getDaterange?.[1];
    }
    if (allowEdit) {
      if (
        !statusModal &&
        ruleData?.rule_group_is_active !== value?.rule_group_is_active
      ) {
        setStatusModal(true);
        setSaveLoading(false);
        return;
      }
      mutate(
        {
          resource: `${type === 'Show' && rulePlanDataObj?.view_unique_id ? apiEndpoint.ruleGroup : apiEndpoint.ruleGroupDraft}`,
          values: {
            ...value,
            rule_plan: rulePlanDataObj?.unique_id,
            rule_group_start_date: dayjs(startDate).format('YYYY-MM-DD'),
            rule_group_end_date: dayjs(endDate).format('YYYY-MM-DD'),
          },
          id: editId,
          successNotification: false,
          errorNotification: false,
        },
        {
          onSuccess: (data) => {
            const message =
              data?.data?.message || t('notifications.createSuccess');
            // prettier-ignore
            openNotificationWithIcon(constants.success, t('notifications.mutationSuccess'), message);
            setRefetchRuleDetails(!refetchRuleDetails);
            setSaveLoading(false);
          },
          onError(error) {
            const message = error?.message || t('notifications.somethingError');

            // prettier-ignore
            openNotificationWithIcon(constants.error, t('notifications.mutationError'), message);
            setSaveLoading(false);
          },
        }
      );
    } else {
      await onFinish({
        ...value,
        rule_plan: rulePlanDataObj?.unique_id,
        rule_group_start_date: dayjs(startDate).format('YYYY-MM-DD'),
        rule_group_end_date: dayjs(endDate).format('YYYY-MM-DD'),
      });
      setSaveLoading(false);
    }
    resetFieldsWithoutDate(
      form,
      rulePlanDataObj.rule_plan_start_date,
      rulePlanDataObj.rule_plan_end_date
    );
    await setAllowEdit(false);
  };
  const hideModal = () => {
    setOpenModal(false);
  };
  const onEdit = (val: string) => {
    setEditId(val);
  };
  useEffect(() => {
    if (type === 'Edit' || type === 'Show') {
      onEdit(id);
      setAllowEdit(true);
    }
  }, [type]);

  const handleOnChange = () => {
    setPageType('Edit');
  };

  useEffect(() => {
    if (ruleData) {
      const startDate = dayjs(ruleData.rule_group_start_date);
      const endDate = dayjs(ruleData.rule_group_end_date);
      form.setFieldsValue({
        ...ruleData,
        rule_group_date: [startDate, endDate],
      });
    }
  }, [ruleData]);
  useEffect(() => {
    if (rulePlanDataObj) {
      const startDate = dayjs(rulePlanDataObj.rule_plan_start_date);
      const endDate = dayjs(rulePlanDataObj.rule_plan_end_date);
      setRuleGroupDate([startDate, endDate]);
      if (!ruleData) {
        form.setFieldsValue({
          ...ruleData,
          rule_group_date: [startDate, endDate],
        });
      }
    }
  }, [rulePlanDataObj]);
  const disabled = false;

  const groupHasRules = rulesData?.rule_group_list?.filter(
    (item) => item?.unique_id === editId
  )[0]?.rule_list?.length;
  const hideRulePlanModal = () => {
    setStatusModal(false);
  };
  if (ruleGroupFetching) return <Loading />;

  return (
    <div
      className={`relative ${pageType === 'Show' ? 'rule-details-view' : ''}`}
    >
      <Card>
        <div className="flex gap-md">
          <div className="w-[calc(100%-100px)] ">
            <RuleGroupsForm
              formData={{
                onFinish,
                formLoading,
                formProps,
                saveButtonProps,
              }}
              eventHandler={{ onFinishHandler }}
              stateData={{ ruleGroupDate, saveLoading }}
              staticData={{ type: pageType, groupHasRules }}
              configData={{ disabled }}
            />
          </div>
          <CustomModal
            openModal={openModal}
            hideModal={hideModal}
            title={t('common.note')}
            message={t('ruleGroup.rulegrpUnsavedChanges')}
          />
          <CustomModal
            openModal={statusModal}
            hideModal={hideRulePlanModal}
            title={t('common.note')}
            message={
              ruleData?.rule_group_is_active
                ? t('ruleGroup.messages.ruleGroupInactiveAlert')
                : t('ruleGroup.messages.ruleGroupActiveAlert')
            }
            footer={getFooterButton(
              ruleData?.rule_group_is_active, // true means old state is active and new state is  inactive
              saveButtonProps,
              form,
              onFinishHandler,
              t
            )}
          />
        </div>
      </Card>
      {type !== 'Create' && type !== 'Show' && pageType !== 'Edit' ? (
        // <div onClick={() => handleOnChange()}>Change to Edit</div>
        <EditOutlined
          className="text-primary-500 text-md absolute right-4 top-4 w-10 h-10 flex justify-center items-center border border-solid border-primary-500 rounded-md cursor-pointer"
          onClick={() => handleOnChange()}
        />
      ) : null}
    </div>
  );
};
export default RuleGroupsCreate;
